// Auto-reload on stale dynamic imports after deployments.
// When a new deploy removes old hashed chunks, users with a cached shell get
// "Failed to fetch dynamically imported module". This interceptor catches that
// and reloads the page once so they get the latest index.html.
const STALE_CHUNK_RELOADED_KEY = 'stale-chunk-reloaded';
window.addEventListener('error', (event) => {
  const msg = event.message?.toLowerCase() || '';
  if (
    (msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('importing a module script failed')) &&
    !sessionStorage.getItem(STALE_CHUNK_RELOADED_KEY)
  ) {
    sessionStorage.setItem(STALE_CHUNK_RELOADED_KEY, '1');
    window.location.reload();
  }
});

// Suppress known Capacitor plugin errors on platforms that don't implement them.
// e.g., "PushNotifications.then() is not implemented on android"
window.addEventListener('unhandledrejection', (event) => {
  if (
    typeof event.reason === 'string' &&
    event.reason.includes('is not implemented')
  ) {
    event.preventDefault();
  }
});

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from './contexts/auth-context';
import { ThemeProvider } from './contexts/theme-provider';
import './index.css';
import { Toaster } from 'sonner';
import { PWAInstallButton } from './components/shared/pwa-install-button';
import { registerInstallPrompt } from './utils/pwa-install-prompt';
import './i18n'; // Initialize i18n
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/index';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 1 minute (reduced from 5)
      staleTime: 1000 * 60, // 1 minute
      // Keep unused data in cache for 5 minutes to prevent memory leaks
      gcTime: 1000 * 60 * 5, // 5 minutes
      // Don't refetch on window focus for better UX
      refetchOnWindowFocus: false,
      // Retry failed queries once with exponential backoff
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on reconnect
      refetchOnReconnect: 'always',
    },
    mutations: {
      // Retry mutations once for transient failures
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Import the generated route tree
import { routeTree } from './routeTree.gen';

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  // console.log('[SW] Service Worker support detected');
  window.addEventListener('load', () => {
    // console.log('[SW] Registering service worker...');
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        // console.log('[SW] ✓ Service Worker registered successfully');
        // console.log('[SW] Registration scope:', registration.scope);
        // console.log('[SW] Registration active:', registration.active);
        // console.log('[SW] Registration installing:', registration.installing);
        // console.log('[SW] Registration waiting:', registration.waiting);

        // Check for updates periodically
        setInterval(
          () => {
            // console.log('[SW] Checking for service worker updates...');
            registration.update();
          },
          60 * 60 * 1000,
        ); // Check every hour
      })
      .catch((error) => {
        // console.error('[SW] ✗ Service Worker registration failed:', error);
        if (error instanceof Error) {
          // console.error('[SW] Error message:', error.message);
          // console.error('[SW] Error stack:', error.stack);
        }
      });
  });
} else {
  // console.warn('[SW] ✗ Service Worker not supported in this browser');
}

// Register PWA install prompt handler
registerInstallPrompt();

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    "Root element not found. Check if it's in your index.html or if the id is correct.",
  );
}

// Render the app
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="light">
            <AuthProvider>
              <Toaster />
              <RouterProvider router={router} />
              <PWAInstallButton />
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </StrictMode>,
  );
}
